import clsx from 'clsx';
import type { ReactNode } from 'react';

import { handleTablistKeyDown } from './tablistKeys';

export type SegmentedItem<K extends string | number> = {
  key: K;
  label: ReactNode;
  /** Id panelu, ktorý záložka ovláda (`aria-controls`). */
  controls?: string;
  /** Id samotnej záložky, aby sa naň mohol panel odvolať (`aria-labelledby`). */
  id?: string;
};

/**
 * Prepínač v štýle iOS (`.segmented`) so sémantikou záložiek a šípkami.
 *
 * Appka mala rovnakú lištu ručne napísanú na desiatke miest a ani jedna
 * nereagovala na šípky — z klávesnice sa medzi záložkami chodilo tabulátorom
 * cez každú zvlášť. Toto je jedno miesto, kde je to správne.
 *
 * `scroll` pustí lištu do šírky obrazovky a nechá ju posúvať do strany, keď
 * sa na telefón nezmestí; `fill` roztiahne položky rovnomerne na celú šírku.
 * `size="lg"` dáva na dotykovom displeji cieľ 44 px (odporúčanie Apple);
 * na desktope ostáva kompaktná.
 */
export const SegmentedControl = <K extends string | number>({
  items,
  value,
  onChange,
  ariaLabel,
  activation = 'auto',
  size = 'md',
  scroll = false,
  fill = false,
  className,
}: {
  items: ReadonlyArray<SegmentedItem<K>>;
  /** `null` = nič nie je vybrané (napr. keď je otvorené niečo mimo lišty). */
  value: K | null;
  onChange: (key: K) => void;
  ariaLabel: string;
  activation?: 'auto' | 'manual';
  size?: 'md' | 'lg';
  scroll?: boolean;
  fill?: boolean;
  className?: string;
}) => {
  const focusKey = items.some((item) => item.key === value) ? value : items[0]?.key;
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={clsx(
        'segmented',
        scroll && 'no-scrollbar max-w-full overflow-x-auto',
        fill && 'flex w-full',
        className,
      )}
      onKeyDown={(event) => handleTablistKeyDown(event, { activate: activation === 'auto' })}
    >
      {items.map((item) => (
        <button
          key={item.key}
          id={item.id}
          type="button"
          role="tab"
          aria-selected={item.key === value}
          aria-controls={item.controls}
          tabIndex={item.key === focusKey ? 0 : -1}
          className={clsx(
            'segmented-item shrink-0 whitespace-nowrap',
            size === 'lg' && 'segmented-item-lg',
            fill && 'flex-1',
          )}
          onClick={() => onChange(item.key)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
};
